// Typed view over the meta doc (protocol spec §Doc schema): a root Y.Map
// named `files` mapping `file_id (ULID) → Y.Map { path, kind, hash?,
// deleted_at? }`. The server validates every meta transaction against this
// exact shape + the DESIGN §14 path policy and rejects violations, so we
// build entries as nested Y.Maps and never put anything else in the map.

import * as Y from "yjs";
import { pathKey } from "./conflictPolicy";

export type FileKind = "md" | "attachment";

/** A decoded meta entry. */
export interface FileEntry {
  fileId: string;
  path: string;
  kind: FileKind;
  hash?: string;
  /** unix seconds; present once tombstoned (kept until server GC). */
  deletedAt?: number;
}

/** The root map name (normative). */
export const FILES_MAP = "files";

type EntryMap = Y.Map<string | number>;

function decodeEntry(fileId: string, m: EntryMap): FileEntry | undefined {
  const path = m.get("path");
  const kind = m.get("kind");
  if (typeof path !== "string" || (kind !== "md" && kind !== "attachment")) {
    return undefined;
  }
  const hash = m.get("hash");
  const deletedAt = m.get("deleted_at");
  const entry: FileEntry = { fileId, path, kind };
  if (typeof hash === "string") entry.hash = hash;
  if (typeof deletedAt === "number") entry.deletedAt = deletedAt;
  return entry;
}

/** A read/write view over a meta `Y.Doc`. */
export class MetaView {
  constructor(readonly doc: Y.Doc) {}

  files(): Y.Map<EntryMap> {
    return this.doc.getMap<EntryMap>(FILES_MAP);
  }

  /** All entries, including tombstoned ones. */
  all(): FileEntry[] {
    const out: FileEntry[] = [];
    this.files().forEach((m, fileId) => {
      const e = decodeEntry(fileId, m);
      if (e) out.push(e);
    });
    return out;
  }

  /** Live (non-tombstoned) entries. */
  active(): FileEntry[] {
    return this.all().filter((e) => e.deletedAt === undefined);
  }

  get(fileId: string): FileEntry | undefined {
    const m = this.files().get(fileId);
    return m ? decodeEntry(fileId, m) : undefined;
  }

  /** The live entry currently occupying `path` (exact match), if any. */
  byPath(path: string): FileEntry | undefined {
    return this.active().find((e) => e.path === path);
  }

  /** A live entry whose path collides with `path` under the uniqueness key. */
  collidingWith(path: string, exceptFileId?: string): FileEntry | undefined {
    const key = pathKey(path);
    return this.active().find(
      (e) => e.fileId !== exceptFileId && pathKey(e.path) === key,
    );
  }

  /**
   * Add a new file entry. Must be called inside a transaction (the bridge
   * wraps meta mutations in an ORIGIN_LOCAL transaction so they're sent up).
   */
  add(fileId: string, path: string, kind: FileKind, hash?: string): void {
    const entry = new Y.Map<string | number>();
    entry.set("path", path);
    entry.set("kind", kind);
    if (hash !== undefined) entry.set("hash", hash);
    this.files().set(fileId, entry as EntryMap);
  }

  setPath(fileId: string, path: string): void {
    this.files().get(fileId)?.set("path", path);
  }

  setHash(fileId: string, hash: string): void {
    this.files().get(fileId)?.set("hash", hash);
  }

  /** Tombstone an entry; the server GCs the doc later and emits DocRemoved. */
  tombstone(fileId: string, deletedAt: number): void {
    this.files().get(fileId)?.set("deleted_at", deletedAt);
  }

  /** Remove the entry outright (used on GC observation). */
  remove(fileId: string): void {
    this.files().delete(fileId);
  }
}

/** The content-doc root text name (normative). */
export const CONTENT_TEXT = "content";

/** The root `Y.Text` of a content doc. */
export function contentText(doc: Y.Doc): Y.Text {
  return doc.getText(CONTENT_TEXT);
}
